import * as React from 'react';
import { Button } from './button.js';

// ─── Types ────────────────────────────────────────────────────

interface SaveCampaignDialogProps {
  title: string;
  description: string;
  children?: React.ReactNode;
  cancelLabel: string;
  saveLabel: string;
  onClose: () => void;
  onSave: () => void;
}

// ─── Component ───────────────────────────────────────────────

function SaveCampaignDialog({
  title,
  description,
  children,
  cancelLabel,
  saveLabel,
  onClose,
  onSave,
}: SaveCampaignDialogProps) {
  const titleId = React.useId();
  return (
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-50 bg-black/40"
        onClick={onClose}
        data-testid="campaign-dialog-backdrop"
      />
      {/* TODO: Add focus trap when this component moves to apps/web. Currently has
          aria-modal="true" but no focus trap — Tab key escapes to content behind
          the backdrop. Use shadcn Dialog (which handles this) or add a focus trap
          library (e.g. focus-trap-react). */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm rounded-xl border border-border bg-card shadow-2xl"
        data-testid="campaign-save-dialog"
      >
        <div className="px-5 py-4 border-b border-border/60">
          <h2 id={titleId} className="text-[14px] font-semibold">
            {title}
          </h2>
          <p className="text-[12px] text-muted-foreground mt-0.5">{description}</p>
        </div>
        {children ? <div className="px-5 py-4">{children}</div> : null}
        <div className="flex gap-2 px-5 pb-4">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={onClose}
            data-testid="campaign-dialog-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            variant="default"
            size="sm"
            fullWidth
            onClick={onSave}
            data-testid="campaign-dialog-save"
          >
            {saveLabel}
          </Button>
        </div>
      </div>
    </>
  );
}

export { SaveCampaignDialog };
export type { SaveCampaignDialogProps };
